import { MutableRefObject, ReactEventHandler, useCallback } from "react";
import * as TinyMCETypes from "tinymce";
import { IMessageContainers } from "../../../../MessageComponents/Enums/MessageEnums";
import { MessageStateType } from "../../../Typings/MessageState";
import { IEmailTemplate, ITextTemplate } from "../../../Typings/MessageText";
import {
  closeSendMenu,
  IEmailInput,
  initializeMessageDialogInput,
  inputEmailBody,
  inputEmailSubject,
  inputTextBody,
  openSendMenu,
  resetMessageBody,
  resetSubject,
  selectInputMessageState,
  setMessageType,
} from "../../Slices/MessageDialogInputSlice";
import { useAppDispatch, useAppSelector } from "../reduxHooks";
import { useMessageDialogState } from "./useMessageDialog";

export function useMessageInputState() {
  return useAppSelector(selectInputMessageState);
}
export function useMessageInputHandlers() {
  const dispatch = useAppDispatch();
  const dialogState = useMessageDialogState();
  const inputState = useMessageInputState();

  const handleClearInputState = () => {
    dispatch(resetMessageBody());
    dispatch(resetSubject());
  };

  const handleInitializeInputState = (messageState: MessageStateType) => {
    dispatch(initializeMessageDialogInput(messageState));
  };

  const handleInputTextChange = (value: string) => {
    dispatch(inputTextBody(value));
  };
  const handleInputEmailChange = (
    value: IEmailInput,
    editor?: MutableRefObject<TinyMCETypes.Editor | null>,
    insert?: boolean
  ) => {
    dispatch(inputEmailBody(value));
    if (editor?.current) {
      if (insert) {
        editor.current.insertContent(value.body);
      } else {
        editor.current.setContent(value.body);
      }
    }
  };
  const handleInputSubjectChange = (value: string) => {
    dispatch(inputEmailSubject(value));
  };

  const handleInputBodyChange: ReactEventHandler<HTMLTextAreaElement | HTMLInputElement> = useCallback(
    (e) => {
      if (inputState.messageType === "email") {
        handleInputEmailChange({ body: e.currentTarget.value, bodyText: e.currentTarget.value });
      } else {
        handleInputTextChange(e.currentTarget.value);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputState.messageType]
  );

  const handleCloseSendMenu = () => {
    dispatch(closeSendMenu());
  };
  const handleOpenSendMenu = () => {
    dispatch(openSendMenu());
  };

  const handleChangeMessageType = (type: IMessageContainers) => {
    if (IMessageContainers[type].includes("Text")) {
      dispatch(setMessageType("text"));
    } else {
      dispatch(setMessageType("email"));
    }
  };

  const handleApplyEmailTemplate = (
    template: IEmailTemplate,
    editor?: MutableRefObject<TinyMCETypes.Editor | null>
  ) => {
    handleInputEmailChange({ body: template.body, bodyText: template.body }, editor);
    handleInputSubjectChange(template.subject);
  };
  const handleApplyTextTemplate = (template: ITextTemplate) => {
    handleInputTextChange(template.body);
  };

  const handleAddTemplateField = useCallback(
    (field: string, focusedInput?: HTMLElement | null) => {
      if (inputState.messageType === "email") {
        if (inputState.activeInput === "subject") {
          handleInputSubjectChange(`${inputState.subject} [[${field}]]`);
        } else {
          const emailBody = `${inputState.body} [[${field}]]`;
          handleInputEmailChange({ body: emailBody, bodyText: emailBody });
        }
      } else {
        dispatch(inputTextBody(`${inputState.body} [[${field}]]`));
      }
      focusedInput?.focus();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputState.activeInput, inputState.body, inputState.messageType, inputState.subject]
  );

  const handleValidateMessageInput = useCallback(() => {
    if (IMessageContainers[dialogState.containerType].includes("Email")) {
      return (
        !!inputState.body.trim() &&
        !!inputState.subject?.trim() &&
        !inputState.bodyErrorMessage &&
        !inputState.subjectErrorMessage
      );
    } else {
      return !!inputState.body.trim() && !inputState.bodyErrorMessage;
    }
  }, [
    dialogState.containerType,
    inputState.body,
    inputState.bodyErrorMessage,
    inputState.subject,
    inputState.subjectErrorMessage,
  ]);

  return {
    handleClearInputState,
    handleChangeMessageType,
    handleAddTemplateField,
    handleApplyEmailTemplate,
    handleApplyTextTemplate,
    handleCloseSendMenu,
    handleInputBodyChange,
    handleInputTextChange,
    handleInputEmailChange,
    handleInputSubjectChange,
    handleOpenSendMenu,
    handleInitializeInputState,
    handleValidateMessageInput,
  } as const;
}
